/**
 * Right panel — Events / Alerts / AI Intelligence tab switch
 * (mockup `rp-tabbar` + `setRpTab`, fsl-mocks commit 88097d2).
 *
 * Replaces the old fixed alerts-over-chat split: the full panel height goes to
 * whichever view is active. Unread badges accumulate per tab while another tab
 * is being viewed and clear on switch. Rendered for every context, including
 * inv and apps, which previously had the AI chat only.
 *
 * "Debug Logs" is a 4th pill, offered only while debugMode is on — it replaces
 * the old nested SidebarTabs so the panel never stacks two tab bars.
 */
import { AlertsPane } from '@/features/alerts/AlertsPane';
import { EventsPane } from '@/features/alerts/EventsPane';
import { routeByContext } from '@/features/alerts/routing';
import { AppsIntelligence } from '@/features/apps/AppsIntelligence';
import { InventoryIntelligence } from '@/features/chat/InventoryIntelligence';
import { ServicesIntelligence } from '@/features/chat/ServicesIntelligence';
import { DebugPanel } from '@/features/debug/DebugPanel';
import { RIGHT_PANEL_ENABLED } from '@/features/shell/rightPanelFlag';
import { useAlertFeedStore } from '@/lib/store/alertFeed';
import { useAlertsStore } from '@/lib/store/alerts';
import { useDronesStore } from '@/lib/store/drones';
import { APP_START_MS, type Context, type RightPanelTab, useUiStore } from '@/lib/store/ui';
import { cn } from '@/lib/utils';
import { useEffect } from 'react';

export function RightPanel({ ctx, debugOn }: { ctx: Context; debugOn: boolean }) {
  const tab = useUiStore((s) => s.rightPanelTab);
  const setTab = useUiStore((s) => s.setRightPanelTab);
  const markSeen = useUiStore((s) => s.markRpSeen);
  const lastSeen = useUiStore((s) => s.rpLastSeen[ctx]);
  const selectedDroneId = useDronesStore((s) => s.selectedId);
  const events = useAlertsStore((s) => s.items);
  const alerts = useAlertFeedStore((s) => s.items);

  // Debug pill can vanish (debug mode off, unsupported context) while selected.
  // ENG-282: with the panel flagged off, the only view left is Debug Logs.
  const effectiveTab: RightPanelTab = !RIGHT_PANEL_ENABLED
    ? 'debug'
    : tab === 'debug' && !debugOn
      ? 'ai'
      : tab;

  // Switching to a feed tab — or carrying it into another context — marks that
  // context/tab pair as read, mirroring the mockup's `_rpLastSeen` write.
  useEffect(() => {
    if (effectiveTab === 'events' || effectiveTab === 'alerts') markSeen(ctx, effectiveTab);
  }, [ctx, effectiveTab, markSeen]);

  const unread = (which: 'events' | 'alerts') => {
    if (effectiveTab === which) return 0;
    const since = lastSeen?.[which] ?? APP_START_MS;
    const feed = which === 'events' ? events : alerts;
    return feed.filter(
      (i) => routeByContext(i, ctx, selectedDroneId) && new Date(i.timestamp).getTime() > since,
    ).length;
  };

  return (
    <div className="flex flex-col h-full min-h-0 overflow-hidden">
      <div className="flex items-center gap-1.5 px-3 py-2.5 border-b border-(--color-border) shrink-0 overflow-x-auto">
        {RIGHT_PANEL_ENABLED && (
          <>
            <TabPill
              label="Events"
              on={effectiveTab === 'events'}
              badge={unread('events')}
              onClick={() => setTab('events')}
            />
            <TabPill
              label="Alerts"
              on={effectiveTab === 'alerts'}
              badge={unread('alerts')}
              onClick={() => setTab('alerts')}
            />
            <TabPill
              label="AI Intelligence"
              on={effectiveTab === 'ai'}
              badge={0}
              onClick={() => setTab('ai')}
            />
          </>
        )}
        {debugOn && (
          <TabPill
            label="Debug Logs"
            on={effectiveTab === 'debug'}
            badge={0}
            onClick={() => setTab('debug')}
          />
        )}
      </div>

      <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
        {effectiveTab === 'events' ? (
          <EventsPane />
        ) : effectiveTab === 'alerts' ? (
          <AlertsPane />
        ) : effectiveTab === 'debug' ? (
          <DebugPanel ctx={ctx} />
        ) : ctx === 'inv' ? (
          <InventoryIntelligence />
        ) : ctx === 'apps' ? (
          <AppsIntelligence />
        ) : (
          <ServicesIntelligence />
        )}
      </div>
    </div>
  );
}

/** Mockup `.tgl-radio` — same pill selector used by the SERVICE radios. */
function TabPill({
  label,
  on,
  badge,
  onClick,
}: { label: string; on: boolean; badge: number; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={on}
      className={cn(
        'flex items-center h-7 px-2.5 rounded-[14px] border text-[0.769em] font-semibold font-mono whitespace-nowrap select-none transition-colors',
        on
          ? 'bg-(--color-selected) text-(--color-selected-fg) border-(--color-selected)'
          : 'bg-(--color-bg) border-(--color-border) text-(--color-text2) hover:border-(--color-green)',
      )}
    >
      {label}
      {badge > 0 && (
        <span className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-[9px] bg-(--color-red) text-(--color-on-accent) text-[0.769em] font-bold">
          {badge > 99 ? '99+' : badge}
        </span>
      )}
    </button>
  );
}
